import { captionFontFamilyOptions, defaultCaptionStyleSettings, quoteRuleConstants, } from "@reelmagic/domain";
export function getSceneDurations(draft) {
    return draft.scenes.map((scene) => scene.durationMs);
}
export const previewVideoConfig = {
    fps: 24,
    height: 1920,
    width: 1080,
};
export const previewTailPaddingSeconds = quoteRuleConstants.DRAFT_TAIL_PADDING_MS / 1000;
export const playbackSpeedLimits = {
    default: 1,
    max: 2.5,
    min: 1,
};
export function getDraftPlaybackSpeed(draft) {
    const speed = draft.playbackSpeed ?? playbackSpeedLimits.default;
    if (!Number.isFinite(speed)) {
        return playbackSpeedLimits.default;
    }
    return Math.min(playbackSpeedLimits.max, Math.max(playbackSpeedLimits.min, speed));
}
export function resolveSceneTransition(scene, sceneIndex) {
    if (sceneIndex === 0) {
        return "none";
    }
    return isSceneTransition(scene?.transitionIn) ? scene.transitionIn : "none";
}
export function resolveSceneAnimation(scene) {
    return isSceneAnimation(scene?.sceneAnimation) ? scene.sceneAnimation : "none";
}
export function getSceneTransitionDurationFrames(transition, fps = previewVideoConfig.fps) {
    if (transition === "none") {
        return 0;
    }
    return Math.max(1, Math.round(0.38 * fps));
}
export function msToFrames(durationMs, fps = previewVideoConfig.fps) {
    return Math.max(1, Math.round((Math.max(durationMs, 0) / 1000) * fps));
}
export function buildDraftPreviewTimeline(draft, fps = previewVideoConfig.fps) {
    const sceneTimings = [];
    let frameCursor = 0;
    const playbackSpeed = getDraftPlaybackSpeed(draft);
    for (const scene of draft.scenes) {
        const durationFrames = msToFrames(scene.durationMs / playbackSpeed, fps);
        sceneTimings.push({
            sceneId: scene.sceneId,
            scriptText: scene.scriptText,
            captionText: scene.captionText,
            captionWords: scene.captionWords ?? [],
            startFrame: frameCursor,
            durationFrames,
        });
        frameCursor += durationFrames;
    }
    const tailPaddingFrames = msToFrames(previewTailPaddingSeconds * 1000, fps);
    return {
        durationInFrames: Math.max(frameCursor + tailPaddingFrames, fps),
        sceneTimings,
    };
}
export function resolveCaptionTimingMode(draft) {
    return draft.captionTimingMode ?? defaultCaptionStyleSettings.captionTimingMode;
}
export function resolveCaptionAnimation(draft) {
    return isCaptionAnimation(draft.captionAnimation)
        ? draft.captionAnimation
        : defaultCaptionStyleSettings.captionAnimation;
}
export function resolveCaptionFontFamily(draft) {
    return isCaptionFontFamily(draft.captionFontFamily)
        ? draft.captionFontFamily
        : defaultCaptionStyleSettings.captionFontFamily;
}
export function resolveCaptionTextColor(draft) {
    return draft.captionTextColor ?? defaultCaptionStyleSettings.captionTextColor;
}
export function resolveCaptionActiveTextColor(draft) {
    return draft.captionActiveTextColor ?? defaultCaptionStyleSettings.captionActiveTextColor;
}
export function resolveCaptionOutlineColor(draft) {
    return draft.captionOutlineColor ?? defaultCaptionStyleSettings.captionOutlineColor;
}
export function resolveCaptionOutlineWidth(draft) {
    const width = draft.captionOutlineWidth ?? defaultCaptionStyleSettings.captionOutlineWidth;
    if (!Number.isFinite(width)) {
        return defaultCaptionStyleSettings.captionOutlineWidth;
    }
    return Math.min(36, Math.max(0, width));
}
export function resolveCaptionFontSize(draft) {
    const fallback = draft.captionPreset === "bold" ? 64 : defaultCaptionStyleSettings.captionFontSize;
    const size = draft.captionFontSize ?? fallback;
    if (!Number.isFinite(size)) {
        return fallback;
    }
    return Math.min(112, Math.max(32, size));
}
export const captionFontFamilyCss = {
    system: "ui-sans-serif, system-ui, sans-serif",
    inter: "'Inter', ui-sans-serif, system-ui, sans-serif",
    montserrat: "'Montserrat', ui-sans-serif, system-ui, sans-serif",
    anton: "'Anton', Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif",
    archivo_black: "'Archivo Black', Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif",
    bangers: "'Bangers', Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif",
    bebas_neue: "'Bebas Neue', Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif",
    luckiest_guy: "'Luckiest Guy', Impact, Haettenschweiler, 'Arial Narrow Bold', sans-serif",
    oswald: "'Oswald', ui-sans-serif, system-ui, sans-serif",
    permanent_marker: "'Permanent Marker', cursive",
    poppins: "'Poppins', ui-sans-serif, system-ui, sans-serif",
    roboto_condensed: "'Roboto Condensed', ui-sans-serif, system-ui, sans-serif",
    rubik_mono_one: "'Rubik Mono One', ui-sans-serif, system-ui, sans-serif",
};
export const captionFontStylesheetHref = "https://fonts.googleapis.com/css2?family=Anton&family=Archivo+Black&family=Bangers&family=Bebas+Neue&family=Inter:wght@400;700;800&family=Luckiest+Guy&family=Montserrat:wght@400;700;800&family=Oswald:wght@400;700&family=Permanent+Marker&family=Poppins:wght@400;700;800&family=Roboto+Condensed:wght@400;700;800&family=Rubik+Mono+One&display=swap";
export const captionFontLoadFamilies = [
    "Anton",
    "Archivo Black",
    "Bangers",
    "Bebas Neue",
    "Inter",
    "Luckiest Guy",
    "Montserrat",
    "Oswald",
    "Permanent Marker",
    "Poppins",
    "Roboto Condensed",
    "Rubik Mono One",
];
function isCaptionFontFamily(value) {
    return (typeof value === "string" &&
        captionFontFamilyOptions.some((option) => option.value === value));
}
function isCaptionAnimation(value) {
    return value === "pop" || value === "rise" || value === "punch" || value === "word_pop";
}
function isSceneTransition(value) {
    return (value === "none" ||
        value === "fade" ||
        value === "slide" ||
        value === "zoom" ||
        value === "wipe");
}
function isSceneAnimation(value) {
    return (value === "none" ||
        value === "slow_zoom_in" ||
        value === "slow_zoom_out" ||
        value === "pan_left" ||
        value === "camera_shake");
}
const balancedCaptionWordsPerChunk = 5;
export function getCaptionDisplayWords(input) {
    if (input.scene.captionWords.length === 0) {
        return splitCaptionText(input.scene.captionText).map((text, index) => ({
            isActive: false,
            sourceIndex: index,
            startMs: 0,
            text,
        }));
    }
    const fps = input.fps ?? previewVideoConfig.fps;
    const speed = typeof input.playbackSpeed === "number" && Number.isFinite(input.playbackSpeed)
        ? Math.min(playbackSpeedLimits.max, Math.max(playbackSpeedLimits.min, input.playbackSpeed))
        : playbackSpeedLimits.default;
    const compositionElapsedMs = Math.max(0, ((input.frame - input.scene.startFrame) / fps) * 1000);
    /** Position on the transcript / alignment timeline (matches sped-up audio). */
    const alignmentTimelineMs = compositionElapsedMs * speed;
    const activeIndex = findActiveCaptionWordIndex(input.scene.captionWords, alignmentTimelineMs);
    const activeTimingGroup = getCaptionTimingGroup(input.scene.captionWords, activeIndex);
    if (input.timingMode === "full_scene") {
        return input.scene.captionWords.map((word, index) => ({
            isActive: isCaptionWordInTimingGroup(index, activeTimingGroup),
            sourceIndex: index,
            startMs: word.startMs,
            text: word.text,
        }));
    }
    if (input.timingMode === "word_by_word") {
        return input.scene.captionWords
            .slice(activeTimingGroup.startIndex, activeTimingGroup.endIndex + 1)
            .map((word, index) => ({
            isActive: true,
            sourceIndex: activeTimingGroup.startIndex + index,
            startMs: word.startMs,
            text: word.text,
        }));
    }
    const windowStart = Math.floor(activeIndex / balancedCaptionWordsPerChunk) * balancedCaptionWordsPerChunk;
    const windowEnd = Math.min(input.scene.captionWords.length, windowStart + balancedCaptionWordsPerChunk);
    return input.scene.captionWords.slice(windowStart, windowEnd).map((word, index) => ({
        isActive: isCaptionWordInTimingGroup(windowStart + index, activeTimingGroup),
        sourceIndex: windowStart + index,
        startMs: word.startMs,
        text: word.text,
    }));
}
export function getCaptionEntranceStartFrame(input) {
    if (input.scene.captionWords.length === 0 || input.timingMode === "full_scene") {
        return input.scene.startFrame;
    }
    const fps = input.fps ?? previewVideoConfig.fps;
    const speed = typeof input.playbackSpeed === "number" && Number.isFinite(input.playbackSpeed)
        ? Math.min(playbackSpeedLimits.max, Math.max(playbackSpeedLimits.min, input.playbackSpeed))
        : playbackSpeedLimits.default;
    const compositionElapsedMs = Math.max(0, ((input.frame - input.scene.startFrame) / fps) * 1000);
    const alignmentTimelineMs = compositionElapsedMs * speed;
    const activeIndex = findActiveCaptionWordIndex(input.scene.captionWords, alignmentTimelineMs);
    const sourceMsToCompositionFrames = (sourceMs) => Math.round((sourceMs / speed / 1000) * fps);
    if (input.timingMode === "word_by_word") {
        return (input.scene.startFrame +
            sourceMsToCompositionFrames(input.scene.captionWords[activeIndex]?.startMs ?? 0));
    }
    const windowStart = Math.floor(activeIndex / balancedCaptionWordsPerChunk) * balancedCaptionWordsPerChunk;
    return (input.scene.startFrame +
        sourceMsToCompositionFrames(input.scene.captionWords[windowStart]?.startMs ?? 0));
}
function findActiveCaptionWordIndex(words, relativeMs) {
    const exactIndex = words.findIndex((word) => relativeMs >= word.startMs && relativeMs < word.endMs);
    if (exactIndex >= 0) {
        return exactIndex;
    }
    for (let index = words.length - 1; index >= 0; index -= 1) {
        const word = words[index];
        if (word && word.startMs <= relativeMs) {
            return index;
        }
    }
    return 0;
}
function getCaptionTimingGroup(words, activeIndex) {
    const activeWord = words[activeIndex];
    if (!activeWord) {
        return { endIndex: activeIndex, startIndex: activeIndex };
    }
    let startIndex = activeIndex;
    let endIndex = activeIndex;
    while (startIndex > 0 &&
        words[startIndex - 1]?.startMs === activeWord.startMs &&
        words[startIndex - 1]?.endMs === activeWord.endMs) {
        startIndex -= 1;
    }
    while (endIndex < words.length - 1 &&
        words[endIndex + 1]?.startMs === activeWord.startMs &&
        words[endIndex + 1]?.endMs === activeWord.endMs) {
        endIndex += 1;
    }
    return { endIndex, startIndex };
}
function isCaptionWordInTimingGroup(index, group) {
    return index >= group.startIndex && index <= group.endIndex;
}
function splitCaptionText(value) {
    const words = value.trim().split(/\s+/u).filter(Boolean);
    return words.length > 0 ? words : [value];
}
